import { Button, FormWarning, TextInput } from "@shltelemedicine/shl-ui-lib";
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState
} from "react";
import CountColumn from "../CountColumn";
import styles from "./EditableListItem.module.css";

export type ListItemProps = {
  id: string;
  count?: number | string;
  initialValue?: string;
  focusInput?: boolean;
  disableEdit?: boolean;
  divide?: boolean;
  onChange?: (id: string, value: string) => void;
  onRemove?: (id: string) => void;
  validateInput?: (
    id: string,
    value: string
  ) => { isValid: boolean; errorMessage?: ReactNode };
};

const EditableListItem = ({
  id,
  count,
  initialValue = "",
  focusInput,
  disableEdit,
  divide,
  onChange,
  onRemove,
  validateInput,
  children,
}: PropsWithChildren<ListItemProps>) => {
  const [isEdit, setIsEdit] = useState(false);
  const [inputValue, setInputValue] = useState<string>(initialValue);
  const [isInputValid, setIsInputValid] = useState(true);
  const [invalidInputMessage, setInvalidInputMessage] = useState<ReactNode>("");
  const [lastSavedValue, setLastSavedValue] = useState<string>(initialValue);
  const inputRef = useRef<HTMLInputElement>();

  useEffect(() => {
    if (isEdit) {
      focusTextInput();
    } else {
      setIsInputValid(true);
      setInvalidInputMessage(undefined);
    }
  }, [isEdit]);

  useEffect(() => {
    if (focusInput) {
      setIsEdit(true);
    }
  }, [focusInput]);

  useEffect(() => {
    setInputValue(initialValue);
    setLastSavedValue(initialValue);
  }, [initialValue]);

  const focusTextInput = () => inputRef.current?.focus();

  const inputChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    handelValidation(value);

    setInputValue(value);
  };

  const saveChangeHandler = () => {
    if (!isInputValid) {
      return;
    }

    setLastSavedValue(inputValue);
    setIsEdit(false);

    if (onChange) onChange(id, inputValue);
  };

  const cancelChangeHandler = () => {
    setInputValue(lastSavedValue);
    setIsEdit(false);
  };

  const removeItemHandler = () => {
    if (onRemove) onRemove(id);
  };

  const handelValidation = (value: string) => {
    if (validateInput) {
      const { isValid, errorMessage } = validateInput(id, value);
      setIsInputValid(isValid);
      setInvalidInputMessage(errorMessage);
    }
  };

  return (
    <li className={`py-2 ${styles.item} ${!inputValue && styles.noValue}`}>
      <div className="d-flex align-items-center">
        <div className="d-flex align-items-center flex-grow-1">
          {/* Counter */}
          {count && (
            <>
              <CountColumn count={count} />
              <span className="mx-1"></span>
            </>
          )}

          {!isEdit ? (
            <div onClick={() => !disableEdit && setIsEdit(true)}>
              {children}
            </div>
          ) : (
            <>
              <div className="w-50">
                <TextInput
                  ref={inputRef}
                  value={inputValue}
                  onChange={inputChangeHandler}
                  onEnterKey={saveChangeHandler}
                  onEscKey={cancelChangeHandler}
                  onBlur={cancelChangeHandler}
                  isInvalid={!isInputValid}
                />
              </div>
              <div className="mx-1"></div>
              {!isInputValid && invalidInputMessage && (
                <FormWarning
                  message={invalidInputMessage}
                  className="font-sm"
                />
              )}
            </>
          )}
        </div>
        {!isEdit && inputValue && (
          <>
            {!disableEdit && (
              <Button
                variant=""
                className="p-2"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  removeItemHandler();
                }}
              >
                <i className="fa fa-minus-square"></i>
              </Button>
            )}
          </>
        )}
        {isEdit && (
          <>
            {isInputValid && (
              <Button
                variant=""
                className="p-2"
                onMouseDown={(e) => {
                  e.stopPropagation();
                  saveChangeHandler();
                }}
              >
                <i className="fa fa-check-square"></i>
              </Button>
            )}
            <Button
              variant=""
              className="p-2"
              onMouseDown={(e) => {
                e.stopPropagation();
                cancelChangeHandler();
              }}
            >
              <i className="fa fa-window-close"></i>
            </Button>
          </>
        )}
      </div>
    </li>
  );
};

export default EditableListItem;
